import { DependencyList, useEffect, useRef, useState } from "react";
import { resizeObserverManager } from "@app/shared/qes-standards/utility/ResizeObserverManager";

interface ColumnHighlightRect {
  height: number;
  left: number;
  position: "absolute" | "fixed";
  top: number;
  width: number;
}

/**
 * Gives styling for a rectangle bounding the specified columns of a Waffles table.
 * Note that this is not reactive to the user manually resizing columns.
 * @param containerRef Ref to an element wrapping the waffles table to highlight.
 * @param columnKeys The data-key values of columns to create rect around. Any missing columns are ignored.
 * @param deps An array of objects that, when changed, should force a refresh of the rect.
 * @returns CSS styling that can be used to draw a rectangular overlay over the given columns.
 * @example <div className="rect-overlay" style={columnHighlightRect} />
 */
export const useColumnHighlightRect = (
  containerRef: React.RefObject<HTMLElement>,
  columnKeys: readonly string[],
  deps: DependencyList
): ColumnHighlightRect | null => {
  const [rect, setRect] = useState<ColumnHighlightRect | null>(null);
  const observedHeaderCellsRef = useRef<Set<HTMLElement>>(new Set());
  const observedFakeTableRef = useRef<HTMLElement | null>(null);
  const fakeTableObserverRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      // Waffles shows a sticky "fake" duplicate header (.fake-table, position: fixed) once the real header scrolls
      // out of view, and hides the real one. While that's active, the box must track the fixed fake header instead
      // of the real one, and position itself must be "fixed" too rather than relative to the scrolling container.
      const fakeTable = container.querySelector<HTMLElement>(".fake-table");
      const isStickyHeaderActive = fakeTable?.classList.contains("fix") ?? false;
      const headerScope = isStickyHeaderActive ? ".fake-table" : ".waffles-table";

      const headerCells = columnKeys
        .map((key) => container.querySelector<HTMLElement>(`${headerScope} th[data-key="${key}"]`))
        .filter((el): el is HTMLElement => el !== null);
      const lastBodyCells = columnKeys
        .map((key) => {
          const cells = container.querySelectorAll<HTMLElement>(`.waffles-table td[data-key="${key}"]`);
          return cells.length ? cells[cells.length - 1] : null;
        })
        .filter((el): el is HTMLElement => el !== null);

      if (!headerCells.length || !lastBodyCells.length) {
        setRect(null);
      } else {
        const headerRects = headerCells.map((el) => el.getBoundingClientRect());
        const lastBodyRects = lastBodyCells.map((el) => el.getBoundingClientRect());

        let left = Math.min(...headerRects.map((r) => r.left));
        let right = Math.max(...headerRects.map((r) => r.right));
        let top = Math.min(...headerRects.map((r) => r.top));
        let bottom = Math.max(...lastBodyRects.map((r) => r.bottom));

        const waffleHorizontalScrollRegion = container.querySelector<HTMLElement>(".waffles-container");
        if (waffleHorizontalScrollRegion) {
          const scrollRegionRect = waffleHorizontalScrollRegion.getBoundingClientRect();
          left = Math.max(left, scrollRegionRect.left);
          right = Math.min(right, scrollRegionRect.right);
          top = Math.max(top, scrollRegionRect.top);
          bottom = Math.min(bottom, scrollRegionRect.bottom);
        }

        if (right <= left || bottom <= top) {
          // Fully horizontally scrolled out of view - no rect.
          setRect(null);
        } else if (isStickyHeaderActive) {
          // .fake-table is position: fixed, so viewport coordinates (from getBoundingClientRect) are already the
          // right coordinates — no need to subtract the (scrolling) container's offset.
          setRect({
            height: bottom - top,
            left,
            position: "fixed",
            top,
            width: right - left
          });
        } else {
          const containerRect = container.getBoundingClientRect();
          setRect({
            height: bottom - top,
            left: left - containerRect.left,
            position: "absolute",
            top: top - containerRect.top,
            width: right - left,
          });
        }

        // Column-resize-drag changes a header cell's own width without necessarily resizing the container, so
        // whichever header cells are the current visual source of truth (real or fake) need their own observers.
        const currentHeaderCells = new Set(headerCells);
        observedHeaderCellsRef.current.forEach((el) => {
          if (!currentHeaderCells.has(el)) resizeObserverManager.unobserve(el);
        });
        currentHeaderCells.forEach((el) => {
          if (!observedHeaderCellsRef.current.has(el)) resizeObserverManager.observe(el, measure);
        });
        observedHeaderCellsRef.current = currentHeaderCells;
      }

      if (fakeTable !== observedFakeTableRef.current) {
        fakeTableObserverRef.current?.disconnect();
        if (fakeTable) {
          const observer = new MutationObserver(measure);
          observer.observe(fakeTable, { attributeFilter: ["class"], attributes: true });
          fakeTableObserverRef.current = observer;
        } else {
          fakeTableObserverRef.current = null;
        }
        observedFakeTableRef.current = fakeTable;
      }
    };

    let scrollRafId: number | null = null;
    const handleScroll = () => {
      if (scrollRafId !== null) return;
      scrollRafId = window.requestAnimationFrame(() => {
        scrollRafId = null;
        measure();
      });
    };

    const initalRafId = window.requestAnimationFrame(measure);
    resizeObserverManager.observe(container, measure);
    window.addEventListener("scroll", handleScroll, { passive: true });  // Vertical scroll
    // React to horizontal scrolling
    container.addEventListener("scroll", handleScroll, { capture: true, passive: true });  // Horizontal scroll

    return () => {
      window.cancelAnimationFrame(initalRafId);
      resizeObserverManager.unobserve(container);
      observedHeaderCellsRef.current.forEach((el) => resizeObserverManager.unobserve(el));
      observedHeaderCellsRef.current = new Set();
      fakeTableObserverRef.current?.disconnect();
      fakeTableObserverRef.current = null;
      observedFakeTableRef.current = null;
      window.removeEventListener("scroll", handleScroll);
      container.removeEventListener("scroll", handleScroll, { capture: true });
      if (scrollRafId !== null) window.cancelAnimationFrame(scrollRafId);
    };
  }, [containerRef, columnKeys, ...deps]);

  return rect;
};
